import { Add, Delete, Edit } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FlexGrid,
  FlexGridCellTemplate,
  FlexGridColumn,
} from "@grapecity/wijmo.react.grid";
import { FlexGridFilter } from "@grapecity/wijmo.react.grid.filter";
import { FlexGridXlsxConverter } from "@grapecity/wijmo.grid.xlsx";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useRef, useState } from "react";
import excel from "src/assets/images/excelIcon.png";
import SimpleColumnPicker from "src/qubCommon/Grids/SimpleColumnPicker";
import {
  formatFloat,
  showFloat,
} from "src/qubCommon/wijmoHelpers/numberMethods";
import ReviewCommentsDialog, {
  ReviewCommentsCell,
} from "./ReviewCommentsDialog";
import {
  REVIEW_STATUS_IDS,
  evaluateApprovedAmountFormula,
  getApprovedAmountCellValue,
  getApprovedAmountForStatusTransition,
  getApprovedAmountFocusTarget,
  getApprovedAmountValidationMessage,
  getReviewStatusByShortcut,
  isPartiallyApprovedStatus,
  shouldValidateApprovedAmountOnBlur,
} from "./expenseReviewRules";
import useReviewGridShortcuts, {
  focusReviewEditorAfterOrder,
  focusReviewGridCell,
  focusReviewGridRow,
  selectReviewGridCell,
} from "./useReviewGridShortcuts";

export { REVIEW_STATUS_IDS } from "./expenseReviewRules";

const rowActionButtonSx = {
  width: 26,
  height: 26,
};

const REVIEW_COLUMN_CLASS = "expense-review-column";
const REVIEW_COLUMN_HEADER_CLASS = "expense-review-column-header";
const REVIEW_COLUMN_BINDINGS = new Set([
  "expStatusAbbr",
  "approvedAmount",
  "statusComments",
]);

export const REVIEW_STATUS_OPTIONS = [
  {
    value: REVIEW_STATUS_IDS.pending.toString(),
    label: "Προς έλεγχο",
    shortcut: "0",
  },
  {
    value: REVIEW_STATUS_IDS.approved.toString(),
    label: "Εγκεκριμένη",
    shortcut: "1",
  },
  {
    value: REVIEW_STATUS_IDS.partiallyApproved.toString(),
    label: "Μερικώς εγκεκριμένη",
    shortcut: "2",
  },
  {
    value: REVIEW_STATUS_IDS.rejected.toString(),
    label: "Απορριφθείσα",
    shortcut: "3",
  },
];

const shortcutKeySx = {
  alignItems: "center",
  backgroundColor: "rgba(37, 99, 235, 0.08)",
  border: "1px solid rgba(37, 99, 235, 0.28)",
  borderRadius: 0.75,
  color: "#1d4ed8",
  display: "inline-flex",
  fontFamily: "inherit",
  fontSize: 10,
  fontWeight: 800,
  justifyContent: "center",
  lineHeight: 1,
  minWidth: 18,
  px: 0.45,
  py: 0.3,
};

const ReviewStatusOptionLabel = ({ label, shortcut }) => (
  <Box component="span" sx={{ alignItems: "center", display: "flex", gap: 0.75 }}>
    <Box component="kbd" sx={shortcutKeySx}>
      {shortcut}
    </Box>
    <Box component="span">{label}</Box>
  </Box>
);

const reviewInputSx = {
  "& .MuiInputBase-root": {
    height: 24,
    minHeight: 24,
    fontSize: 13,
  },
  "& .MuiInputBase-input": {
    px: 0.75,
    py: 0.35,
  },
};

const reviewGridSx = {
  "& .wj-flexgrid.expense-review-hotkeys-enabled:focus-within": {
    outline: "2px solid #2563eb",
    outlineOffset: 1,
  },
  "& .wj-flexgrid .wj-cell.expense-review-column": {
    backgroundColor: "#fff7ed",
    borderColor: "#fed7aa",
  },
  "& .wj-flexgrid .wj-cell.expense-review-column-header": {
    backgroundColor: "#ffedd5",
    borderColor: "#fb923c",
    color: "#9a3412",
    fontWeight: 800,
  },
  "& .wj-flexgrid .wj-cell.expense-review-column.wj-state-selected": {
    backgroundColor: "#fdba74",
    color: "#431407",
  },
  "& .wj-flexgrid .wj-cell.expense-review-column .MuiInputBase-root": {
    backgroundColor: "transparent",
  },
};

const stopGridEvent = (event) => {
  event.stopPropagation();
};

const normalizeStatusValue = (value) =>
  value === undefined || value === null || value === ""
    ? REVIEW_STATUS_IDS.pending.toString()
    : value.toString();

const isHiddenMode = (value) =>
  value?.toString?.().trim?.().toLowerCase?.() === "hidden";

const isTrueValue = (value) =>
  value === true || value?.toString?.().trim?.().toLowerCase?.() === "true";

const statusLabelByValue = REVIEW_STATUS_OPTIONS.reduce(
  (acc, option) => ({
    ...acc,
    [option.value]: option.label,
  }),
  {}
);

const parseAmount = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const normalized = value
    .toString()
    .replace(/\s/g, "")
    .replace("€", "")
    .trim();

  if (!normalized) return null;

  if (
    normalized.includes(",") ||
    /^(\d{1,3}\.)+\d{3}$/.test(normalized)
  ) {
    const amount = formatFloat(normalized);
    return Number.isFinite(amount) ? amount : null;
  }

  const amount = Number(normalized);
  return Number.isFinite(amount) ? amount : null;
};

const formatAmount = (value) => {
  const amount = parseAmount(value);
  return amount === null ? "" : showFloat(amount);
};

const ReadOnlyCell = ({ value, align = "left", muted = false }) => (
  <Box
    sx={{
      alignItems: "center",
      color: muted ? "#667085" : "#111827",
      display: "flex",
      fontSize: 13,
      fontWeight: muted ? 600 : 700,
      height: "100%",
      justifyContent: align === "right" ? "flex-end" : "flex-start",
      overflow: "hidden",
      textAlign: align,
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
    title={value ?? ""}
  >
    {value || "-"}
  </Box>
);

const REVIEW_EDITOR_SELECTOR = "[data-expense-review-editor='true']";

const getReviewEditors = (grid, currentInput) => {
  const hostElement =
    grid?.hostElement ?? currentInput?.closest?.(".wj-flexgrid");

  if (!hostElement) return [];

  return Array.from(hostElement.querySelectorAll(REVIEW_EDITOR_SELECTOR))
    .filter((editor) => !editor.disabled && editor.offsetParent !== null)
    .sort((firstEditor, secondEditor) => {
      const firstOrder = Number(firstEditor.dataset.expenseReviewEditorOrder);
      const secondOrder = Number(secondEditor.dataset.expenseReviewEditorOrder);

      return firstOrder - secondOrder;
    });
};

const focusReviewEditor = (editor) => {
  if (!editor) return;

  editor.focus?.();

  if (editor.dataset.expenseReviewEditorType === "approvedAmount") {
    editor.select?.();
  }
};

const moveToNextReviewEditor = (currentInput, grid) => {
  const editors = getReviewEditors(grid, currentInput);
  const currentIndex = editors.indexOf(currentInput);
  const nextEditor = currentIndex >= 0 ? editors[currentIndex + 1] : null;
  const currentOrder = Number(currentInput?.dataset.expenseReviewEditorOrder);

  const focusNextEditor = (editor) => {
    if (editor?.dataset.expenseReviewEditorType === "statusComments") {
      const editorOrder = Number(editor.dataset.expenseReviewEditorOrder);
      focusReviewGridCell(grid, Math.floor(editorOrder / 10), "statusComments");
      return;
    }

    focusReviewEditor(editor);
  };

  window.requestAnimationFrame(() => {
    if (nextEditor?.isConnected) {
      focusNextEditor(nextEditor);
      return;
    }

    const freshEditors = getReviewEditors(grid, currentInput);
    const fallbackEditor = freshEditors.find((editor) => {
      const editorOrder = Number(editor.dataset.expenseReviewEditorOrder);
      return editorOrder > currentOrder;
    });

    focusNextEditor(fallbackEditor);
  });
};

const finishReviewEditing = ({ grid, input, moveNext = false }) => {
  if (moveNext) {
    moveToNextReviewEditor(input, grid);
  }

  grid?.finishEditing?.();
  input?.blur?.();
};

const ReviewStatusEditor = ({
  value,
  editorOrder,
  onChange,
}) => {
  const [open, setOpen] = useState(false);

  const commitStatus = (nextValue, options) => {
    onChange?.(nextValue, options);
    setOpen(false);
  };

  const handleShortcut = (event) => {
    const statusId = getReviewStatusByShortcut(event.key);
    if (statusId === null) return false;

    event.preventDefault();
    event.stopPropagation();
    commitStatus(statusId.toString(), { moveToNextEditor: true });
    return true;
  };

  return (
    <TextField
      select
      fullWidth
      variant="standard"
      size="small"
      value={value}
      onChange={(event) => commitStatus(event.target.value)}
      onClick={stopGridEvent}
      onKeyDown={(event) => {
        if (!handleShortcut(event)) stopGridEvent(event);
      }}
      InputProps={{ disableUnderline: true }}
      SelectProps={{
        open,
        onOpen: () => setOpen(true),
        onClose: () => setOpen(false),
        MenuProps: {
          MenuListProps: {
            onKeyDown: handleShortcut,
          },
        },
        SelectDisplayProps: {
          "aria-keyshortcuts": "0 1 2 3",
          "data-expense-review-editor": "true",
          "data-expense-review-editor-order": editorOrder,
          "data-expense-review-editor-type": "expStatus",
        },
      }}
      sx={reviewInputSx}
    >
      {REVIEW_STATUS_OPTIONS.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          <ReviewStatusOptionLabel
            label={option.label}
            shortcut={option.shortcut}
          />
        </MenuItem>
      ))}
    </TextField>
  );
};

const ApprovedAmountEditor = ({
  value,
  onCommit,
  totalAmount,
  grid,
  rowIndex,
  editorOrder,
  shouldFocus = false,
  onFocused,
  onValidationError,
}) => {
  const inputRef = useRef(null);
  const skipNextBlurCommitRef = useRef(false);
  const hasEditedRef = useRef(false);
  const [draftValue, setDraftValue] = useState(formatAmount(value));
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (document.activeElement === inputRef.current) return;

    setDraftValue(formatAmount(value));
    hasEditedRef.current = false;
  }, [value]);

  useEffect(() => {
    if (!shouldFocus) return undefined;

    const animationFrame = window.requestAnimationFrame(() => {
      selectReviewGridCell(grid, rowIndex, "approvedAmount");
      focusReviewEditor(inputRef.current);
      onFocused?.();
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [grid, onFocused, rowIndex, shouldFocus]);

  const commitValue = () => {
    const formulaAmount = evaluateApprovedAmountFormula(draftValue);
    const isFormula = formulaAmount !== undefined;
    const amount = isFormula ? formulaAmount : parseAmount(draftValue);
    const formattedValue =
      amount === null ? (isFormula ? draftValue : "") : showFloat(amount);
    const validationMessage =
      isFormula && formulaAmount === null
        ? "Ο υπολογισμός δεν είναι έγκυρος."
        : getApprovedAmountValidationMessage(amount, Number(totalAmount));
    const nextValue = getApprovedAmountCellValue(
      formattedValue,
      validationMessage,
    );

    setDraftValue(nextValue);
    setErrorMessage(validationMessage);
    hasEditedRef.current = false;
    onCommit?.(nextValue);
    if (validationMessage) onValidationError?.(validationMessage);

    return !validationMessage;
  };

  const selectWholeValue = (event) => {
    stopGridEvent(event);
    selectReviewGridCell(grid, rowIndex, "approvedAmount");
    window.requestAnimationFrame(() => {
      inputRef.current?.select?.();
    });
  };

  return (
    <TextField
      fullWidth
      variant="standard"
      size="small"
      value={draftValue}
      error={Boolean(errorMessage)}
      inputRef={inputRef}
      onChange={(event) => {
        setDraftValue(event.target.value);
        hasEditedRef.current = true;
        if (errorMessage) setErrorMessage("");
      }}
      onBlur={() => {
        if (skipNextBlurCommitRef.current) {
          skipNextBlurCommitRef.current = false;
          return;
        }

        if (!shouldValidateApprovedAmountOnBlur(hasEditedRef.current)) return;

        commitValue();
      }}
      onFocus={selectWholeValue}
      onClick={selectWholeValue}
      onMouseDown={stopGridEvent}
      onKeyDown={(event) => {
        stopGridEvent(event);

        if (event.key === "Enter") {
          event.preventDefault();
          const isValid = commitValue();
          if (isValid) {
            skipNextBlurCommitRef.current = true;
            finishReviewEditing({
              grid,
              input: inputRef.current,
              moveNext: true,
            });
          }
        }

        if (event.key === "Escape") {
          event.preventDefault();
          skipNextBlurCommitRef.current = true;
          hasEditedRef.current = false;
          setErrorMessage("");
          setDraftValue(formatAmount(value));
          finishReviewEditing({ grid, input: inputRef.current });
        }
      }}
      InputProps={{
        disableUnderline: true,
        startAdornment: (
          <InputAdornment
            position="start"
            data-review-grid-interactive="true"
            sx={{ mr: 0.4 }}
          >
            <Tooltip title={'Τύπος: ξεκινήστε με "=" (π.χ. =12*0,24/5)'}>
              <Box
                component="span"
                sx={{
                  color: "#2563eb",
                  cursor: "help",
                  fontSize: 11,
                  fontStyle: "italic",
                  fontWeight: 900,
                  lineHeight: 1,
                }}
              >
                fx
              </Box>
            </Tooltip>
          </InputAdornment>
        ),
        inputProps: {
          "data-expense-review-editor": "true",
          "data-expense-review-editor-order": editorOrder,
          "data-expense-review-editor-type": "approvedAmount",
          "aria-invalid": Boolean(errorMessage),
          "aria-label": "Εγκεκριμένο ποσό",
          title: errorMessage || undefined,
          style: { textAlign: "right" },
        },
      }}
      sx={
        errorMessage
          ? [
              reviewInputSx,
              {
                "& .MuiInputBase-root": {
                  backgroundColor: "rgba(211, 47, 47, 0.08)",
                  outline: "1px solid",
                  outlineColor: "error.main",
                },
              },
            ]
          : reviewInputSx
      }
    />
  );
};

const ExpenseDetailsGrid = ({
  rows = [],
  canEdit = false,
  showApprovedAmount = false,
  showReviewStatus = false,
  canReview = false,
  modeConfig = {},
  addExpenseSetting = null,
  isAddExpenseDisabled = false,
  onAddExpense,
  onRowAction,
  onReviewRowChange,
}) => {
  const gridRef = useRef(null);
  const commentFocusRowRef = useRef(-1);
  const shouldAdvanceAfterCommentsRef = useRef(false);
  const [columnPickerRefreshKey, setColumnPickerRefreshKey] = useState(0);
  const [commentSelection, setCommentSelection] = useState(null);
  const [approvedAmountFocusRowId, setApprovedAmountFocusRowId] = useState(null);
  const showAppartmentColumn = !isHiddenMode(modeConfig.appartmentMode);
  const showChildColumn = !isHiddenMode(modeConfig.childMode);
  const showEmployeeColumn = !isHiddenMode(modeConfig.employeeMode);
  const showDateFromColumn = !isHiddenMode(modeConfig.dateFromMode);
  const showDateToColumn = !isHiddenMode(modeConfig.dateToMode);
  const showVatColumn = !isTrueValue(modeConfig.hideVat);
  const forceHiddenCols = useMemo(
    () =>
      [
        !showAppartmentColumn && "appartmentName",
        !showChildColumn && "childFullName",
        !showEmployeeColumn && "employeeFullName",
        !showDateFromColumn && "dateFrom",
        !showDateToColumn && "dateTo",
        !showVatColumn && "vatValue",
        !showReviewStatus && "expStatusAbbr",
        !showReviewStatus && "statusComments",
        !showApprovedAmount && "approvedAmount",
      ].filter(Boolean),
    [
      showAppartmentColumn,
      showChildColumn,
      showEmployeeColumn,
      showDateFromColumn,
      showDateToColumn,
      showVatColumn,
      showReviewStatus,
      showApprovedAmount,
    ],
  );
  const canAddExpense = addExpenseSetting?.can === true;
  const addExpenseText = addExpenseSetting?.txt ?? "Προσθήκη Δαπάνης";

  useEffect(() => {
    if (!gridRef.current) return;

    setColumnPickerRefreshKey((currentKey) => currentKey + 1);
  }, [
    canEdit,
    showAppartmentColumn,
    showApprovedAmount,
    showChildColumn,
    showDateFromColumn,
    showDateToColumn,
    showEmployeeColumn,
    showReviewStatus,
    showVatColumn,
  ]);

  const handleGridInitialized = (grid) => {
    gridRef.current = grid;
    setColumnPickerRefreshKey((currentKey) => currentKey + 1);
  };

  const handleGridFormatItem = (grid, event) => {
    const binding = event.panel?.columns?.[event.col]?.binding;

    if (!REVIEW_COLUMN_BINDINGS.has(binding)) return;

    event.cell.classList.add(REVIEW_COLUMN_CLASS);

    if (event.panel === grid.columnHeaders) {
      event.cell.classList.add(REVIEW_COLUMN_HEADER_CLASS);
    }
  };

  const handleExportExcel = () => {
    if (!gridRef.current) return;

    FlexGridXlsxConverter.saveAsync(
      gridRef.current,
      {
        includeColumnHeaders: true,
        includeStyles: false,
      },
      "ExpenseDetails.xlsx",
    );
  };

  const moveFromStatusToNextEditor = (rowIndex, statusId) => {
    if (isPartiallyApprovedStatus(statusId)) return;

    window.requestAnimationFrame(() => {
      focusReviewGridCell(gridRef.current, rowIndex, "statusComments");
    });
  };

  const handleStatusChange = (
    row,
    value,
    { moveToNextEditor = false, rowIndex = -1 } = {},
  ) => {
    const statusId = value ? Number(value) : null;
    const patch = {
      expStatusId: statusId,
      expStatusAbbr: statusLabelByValue[value] ?? "",
    };

    const approvedAmount = getApprovedAmountForStatusTransition({
      previousStatusId: row.expStatusId,
      nextStatusId: statusId,
      totalAmount: row.totalAmount,
    });

    if (approvedAmount !== undefined) {
      patch.approvedAmount = approvedAmount;
    }

    setApprovedAmountFocusRowId(
      getApprovedAmountFocusTarget(row.id, statusId),
    );
    onReviewRowChange?.(row.id, patch);

    if (moveToNextEditor && rowIndex >= 0) {
      moveFromStatusToNextEditor(rowIndex, statusId);
    }
  };

  useReviewGridShortcuts({
    enabled: canReview,
    getStatusByShortcut: getReviewStatusByShortcut,
    gridRef,
    onApplyStatus: (row, statusId, rowIndex) => {
      handleStatusChange(row, statusId.toString(), {
        moveToNextEditor: true,
        rowIndex,
      });
      return false;
    },
    onEnterSelectedCell: ({ columnBinding, row, rowIndex }) => {
      if (columnBinding !== "statusComments") return false;

      const rowLabel = `Γραμμή δαπάνης ${rowIndex + 1}: ${row?.expTypeName ?? ""}`;
      commentFocusRowRef.current = rowIndex;
      shouldAdvanceAfterCommentsRef.current = false;
      setCommentSelection({
        rowId: row.id,
        rowLabel,
        draft: row?.statusComments ?? "",
      });
      return true;
    },
  });

  const handleApprovedAmountCommit = (row, value) => {
    onReviewRowChange?.(row.id, { approvedAmount: value });
  };

  const handleApplyReviewComments = () => {
    if (!commentSelection || !canReview) return;

    onReviewRowChange?.(commentSelection.rowId, {
      statusComments: commentSelection.draft,
    });
    shouldAdvanceAfterCommentsRef.current = true;
    setCommentSelection(null);
  };

  const handleOpenReviewComments = (selection, rowIndex) => {
    commentFocusRowRef.current = rowIndex;
    shouldAdvanceAfterCommentsRef.current = false;
    focusReviewGridCell(gridRef.current, rowIndex, "statusComments");
    setCommentSelection(selection);
  };

  const handleReviewCommentsExited = () => {
    const editorOrder = commentFocusRowRef.current * 10 + 2;
    const focusWasMoved = shouldAdvanceAfterCommentsRef.current
      ? focusReviewEditorAfterOrder(gridRef.current, editorOrder)
      : focusReviewGridCell(
          gridRef.current,
          commentFocusRowRef.current,
          "statusComments",
        );

    if (!focusWasMoved) {
      focusReviewGridRow(gridRef.current, commentFocusRowRef.current);
    }

    commentFocusRowRef.current = -1;
    shouldAdvanceAfterCommentsRef.current = false;
  };

  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: "#fff",
        borderRadius: "0.25rem",
        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
        p: 1,
        ...reviewGridSx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: 1,
          mb: 1,
        }}
      >
        <SimpleColumnPicker
          ref={gridRef}
          refreshKey={columnPickerRefreshKey}
          storageKey="ExpenseDetailsGrid"
          forceHiddenCols={forceHiddenCols}
        />
        <Box boxShadow={2} sx={{ borderRadius: 50, width: 40, height: 40 }}>
          <Tooltip title="Εξαγωγή σε Excel">
            <IconButton onClick={handleExportExcel}>
              <img src={excel} alt="excel" style={{ height: 24 }} />
            </IconButton>
          </Tooltip>
        </Box>
        {canAddExpense && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<Add />}
            disabled={isAddExpenseDisabled}
            onClick={() => onAddExpense?.()}
            title={`${addExpenseText} (Alt+N)`}
            sx={{
              fontWeight: 800,
              minHeight: 32,
              whiteSpace: "nowrap",
            }}
          >
            {addExpenseText} (Alt+N)
          </Button>
        )}
        {canReview && (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontWeight: 700, ml: "auto" }}
          >
            Hotkeys ενεργού grid: ↑/↓ γραμμή · 0 προς έλεγχο · 1 έγκριση ·
            2 μερική · 3 απόρριψη
          </Typography>
        )}
      </Box>

      <FlexGrid
        autoGenerateColumns={false}
        className="explorer-grid"
        formatItem={handleGridFormatItem}
        headersVisibility="Column"
        initialized={handleGridInitialized}
        isReadOnly
        itemsSource={rows}
        selectionMode="Row"
      >
        <FlexGridFilter />
        {canEdit && (
          <FlexGridColumn
            header=""
            width={78}
            minWidth={78}
            align="center"
            allowSorting={false}
          >
            <FlexGridCellTemplate
              cellType="Cell"
              template={(ctx) => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.25,
                    height: "100%",
                  }}
                >
                  <Tooltip title="Επεξεργασία">
                    <IconButton
                      size="small"
                      color="primary"
                      sx={rowActionButtonSx}
                      onClick={(event) => {
                        event.stopPropagation();
                        onRowAction?.("edit", ctx.item);
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Διαγραφή">
                    <IconButton
                      size="small"
                      color="error"
                      sx={rowActionButtonSx}
                      onClick={(event) => {
                        event.stopPropagation();
                        onRowAction?.("delete", ctx.item);
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            />
          </FlexGridColumn>
        )}
        <FlexGridColumn
          binding="expTypeName"
          header="Είδος Δαπάνης"
          width={220}
          minWidth={180}
        />
        <FlexGridColumn
          binding="netValue"
          header="Αξία"
          format="c€"
          width={130}
          align="right"
        />
        {showVatColumn && (
          <FlexGridColumn
            binding="vatValue"
            header="ΦΠΑ"
            format="c€"
            width={130}
            align="right"
          />
        )}
        <FlexGridColumn
          binding="totalAmount"
          header="Σύνολο"
          format="c€"
          width={140}
          align="right"
        />
        {showReviewStatus && (
          <FlexGridColumn
            binding="expStatusAbbr"
            header="Κατάσταση Ελέγχου"
            width={190}
            minWidth={170}
            allowSorting={!canReview}
            cssClass={REVIEW_COLUMN_CLASS}
          >
            <FlexGridCellTemplate
              cellType="Cell"
              template={(ctx) => {
                const value = normalizeStatusValue(ctx.item?.expStatusId);

                if (!canReview) {
                  return (
                    <ReadOnlyCell
                      value={
                        ctx.item?.expStatusAbbr ??
                        statusLabelByValue[value] ??
                        "Προς έλεγχο"
                      }
                    />
                  );
                }

                return (
                  <ReviewStatusEditor
                    value={value}
                    editorOrder={(ctx.row?.index ?? 0) * 10}
                    onChange={(nextValue, options) =>
                      handleStatusChange(ctx.item, nextValue, {
                        ...options,
                        rowIndex: ctx.row?.index ?? 0,
                      })
                    }
                  />
                );
              }}
            />
          </FlexGridColumn>
        )}
        {showApprovedAmount && (
          <FlexGridColumn
            binding="approvedAmount"
            header="Εγκεκριμένο"
            width={150}
            align="right"
            allowSorting={!canReview}
            cssClass={REVIEW_COLUMN_CLASS}
          >
            <FlexGridCellTemplate
              cellType="Cell"
              template={(ctx) => {
                const statusId = Number(ctx.item?.expStatusId);
                const isApproved = statusId === REVIEW_STATUS_IDS.approved;
                const isPartial = isPartiallyApprovedStatus(statusId);

                if (canReview && isPartial) {
                  return (
                    <ApprovedAmountEditor
                      value={ctx.item?.approvedAmount ?? ""}
                      totalAmount={ctx.item?.totalAmount}
                      grid={ctx.col?.grid}
                      rowIndex={ctx.row?.index ?? 0}
                      editorOrder={(ctx.row?.index ?? 0) * 10 + 1}
                      shouldFocus={approvedAmountFocusRowId === ctx.item?.id}
                      onFocused={() =>
                        setApprovedAmountFocusRowId((currentRowId) =>
                          currentRowId === ctx.item?.id ? null : currentRowId,
                        )
                      }
                      onCommit={(value) =>
                        handleApprovedAmountCommit(ctx.item, value)
                      }
                      onValidationError={(message) =>
                        enqueueSnackbar(message, { variant: "error" })
                      }
                    />
                  );
                }

                if (isApproved) {
                  return (
                    <ReadOnlyCell
                      value={formatAmount(ctx.item?.totalAmount)}
                      align="right"
                    />
                  );
                }

                if (isPartial) {
                  return (
                    <ReadOnlyCell
                      value={formatAmount(ctx.item?.approvedAmount)}
                      align="right"
                    />
                  );
                }

                return <ReadOnlyCell value="-" align="right" muted />;
              }}
            />
          </FlexGridColumn>
        )}
        {showReviewStatus && (
          <FlexGridColumn
            binding="statusComments"
            header="Σχόλια Ελέγχου"
            width={260}
            minWidth={220}
            allowSorting={!canReview}
            cssClass={REVIEW_COLUMN_CLASS}
          >
            <FlexGridCellTemplate
              cellType="Cell"
              template={(ctx) => {
                const rowLabel = `Γραμμή δαπάνης ${ctx.row.index + 1}: ${ctx.item?.expTypeName ?? ""}`;

                return (
                  <ReviewCommentsCell
                    value={ctx.item?.statusComments ?? ""}
                    canEdit={canReview}
                    rowLabel={rowLabel}
                    editorOrder={(ctx.row?.index ?? 0) * 10 + 2}
                    onOpen={() =>
                      handleOpenReviewComments(
                        {
                        rowId: ctx.item.id,
                        rowLabel,
                        draft: ctx.item?.statusComments ?? "",
                        },
                        ctx.row.index,
                      )
                    }
                  />
                );
              }}
            />
          </FlexGridColumn>
        )}
        {showAppartmentColumn && (
          <FlexGridColumn
            binding="appartmentName"
            header="Διαμέρισμα"
            width={180}
          />
        )}
        {showChildColumn && (
          <FlexGridColumn
            binding="childFullName"
            header="Ωφελούμενος"
            width={240}
          />
        )}
        {showEmployeeColumn && (
          <FlexGridColumn
            binding="employeeFullName"
            header="Εργαζόμενος"
            width={190}
          />
        )}
        {showDateFromColumn && (
          <FlexGridColumn
            binding="dateFrom"
            header="Από"
            format="dd/MM/yyyy"
            width={120}
            align="right"
          />
        )}
        {showDateToColumn && (
          <FlexGridColumn
            binding="dateTo"
            header="Έως"
            format="dd/MM/yyyy"
            width={120}
            align="right"
          />
        )}
        <FlexGridColumn binding="expComments" header="Σχόλια" width={220} />
        <FlexGridColumn
          binding="expRemarks"
          header="Παρατηρήσεις"
          width="*"
          minWidth={220}
        />
      </FlexGrid>

      <ReviewCommentsDialog
        selection={commentSelection}
        canEdit={canReview}
        onChange={(draft) =>
          setCommentSelection((current) => ({ ...current, draft }))
        }
        onClose={() => setCommentSelection(null)}
        onApply={handleApplyReviewComments}
        onExited={handleReviewCommentsExited}
      />

      {rows.length === 0 && (
        <Typography
          sx={{
            color: "#6b7280",
            fontSize: 14,
            fontWeight: 600,
            textAlign: "center",
          }}
        >
          Δεν έχουν φορτωθεί γραμμές δαπάνης.
        </Typography>
      )}
    </Box>
  );
};

export default ExpenseDetailsGrid;
